/**
 * FollowingPage · iter150 (2026-02-24)
 *
 * Zeigt alle Creator/Seller denen der User folgt.
 * Datenquelle: GET /api/me/following?area=SOCIAL|MP|STX (Backend liefert getrennt pro Area).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AREAS = [
  { key: "SOCIAL", label: "Social" },
  { key: "MP", label: "Marketplace" },
  { key: "STX", label: "stilltouchedX" },
];

export default function FollowingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("SOCIAL");
  const [data, setData] = useState({ SOCIAL: [], MP: [], STX: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) {
      navigate("/login?next=/following");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled(
          AREAS.map((a) => api.get(`/me/following?area=${a.key}`)),
        );
        const next = { SOCIAL: [], MP: [], STX: [] };
        results.forEach((r, i) => {
          if (r.status === "fulfilled") {
            const d = r.value.data;
            const raw = d?.follows || d?.following || d?.creators || d || [];
            next[AREAS[i].key] = Array.isArray(raw) ? raw : [];
          }
        });
        setData(next);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const list = data[tab] || [];

  return (
    <div data-testid="following-page" className="min-h-screen bg-white">
      <div className="flex items-center gap-3 px-4 h-14 border-b border-[#E5E5E5]">
        <button data-testid="following-back" onClick={() => navigate("/")} className="text-[#0A0A0A]">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-base font-bold flex items-center gap-2"><Users size={16} /> Ich folge</h1>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="inline-flex rounded-full border border-[#E5E5E5] p-0.5 mb-4">
          {AREAS.map((a) => (
            <button
              key={a.key}
              data-testid={`following-tab-${a.key.toLowerCase()}`}
              onClick={() => setTab(a.key)}
              className={`h-8 px-4 rounded-full text-xs font-bold ${
                tab === a.key ? "bg-[#0A0A0A] text-white" : "text-[#666]"
              }`}
            >
              {a.label} <span className="ml-1 opacity-60">({(data[a.key] || []).length})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-[#999]" />
          </div>
        ) : list.length === 0 ? (
          <p data-testid="following-empty" className="text-center text-sm text-[#999] py-16">
            Du folgst noch niemandem in diesem Bereich.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {list.map((c) => {
              const id = c.person_id || c.user_id || c.handle;
              return (
                <button
                  key={id}
                  data-testid={`following-item-${id}`}
                  onClick={() => navigate(`/user/${c.handle || id}`)}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-left"
                >
                  <div className="w-11 h-11 rounded-full bg-[#F5F5F5] overflow-hidden shrink-0">
                    {c.avatar_url ? (
                      <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs font-bold text-[#666]">
                        {(c.display_name || c.handle || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0A0A0A] truncate">
                      {c.display_name || c.handle}
                    </p>
                    {c.subscription_status && (
                      <p className="text-[10px] text-[#999] uppercase tracking-wider">
                        {c.subscription_status}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
